import React, { FunctionComponent, ReactElement, RefObject } from "react";
import { createPortal } from "react-dom";

import { bemClassNames, invariant, usePopupFlip } from "@multiweb/reactUtils";
import { PopupPosition } from "@multiweb/reactUtils/hooks/usePopupFlipHook";

export type PopupComponentProps = {
    /** Reference na spouštěč popupu */
    triggerElement: RefObject<any>;
    /** Reference na obsah popupu */
    contextElement?: RefObject<any>;
    /** Prioritní pozice zobrazení popupu vůči triggerElement  */
    requestedPosition?: PopupPosition;
    /** Příznak, zda je popup otevřený */
    opened?: boolean;
    /** Element popup nabídky */
    children: ReactElement<any>;
    /** Funkce volaná po přesunutí kurzoru mimo Popup */
    onMouseLeave?: () => void;
    /** Funkce volaná po přesunutí kurzoru na Popup */
    onMouseEnter?: () => void;
    /** Id elementu pro vykreslení modálu */
    rootId: string;
    /** Výška obsahu popupu */
    height?: number;
    /** Šířka obsahu popupu */
    width?: number;
};

const className = bemClassNames("popup");

/** Obecná komponenta popupu */
export const PopupComponent: FunctionComponent<PopupComponentProps> = ({
    triggerElement,
    contextElement,
    requestedPosition,
    opened = false,
    children,
    onMouseLeave,
    onMouseEnter,
    rootId,
    height,
    width,
}) => {
    const popupRoot = document.getElementById(rootId);
    const { topOffset, leftOffset } = usePopupFlip({ triggerRef: triggerElement, popupRef: contextElement, positionPriority: requestedPosition, opened, height, width });

    invariant(popupRoot, `[ModalWindow] Cannot render modal window because element with id ${rootId} is undefined.`);

    /**
     * Handler pro událost onMouseLeave
     * pokud je popupu předaná funkce onMouseLeave, zavolá ji
     */
    const handleMouseLeave = () => {
        if (onMouseLeave)
            onMouseLeave();
    };

    /**
     * Handler pro událost onMouseEnter
     * pokud je popupu předaná funkce onMouseEnter, zavolá ji
     */
    const handleMouseEnter = () => {
        if (onMouseEnter)
            onMouseEnter();
    };

    return (createPortal(
        opened && <div className={className()} style={{ top: topOffset, left: leftOffset }} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter}>
            {children}
        </div>, popupRoot)
    );
};
